// Shared FSA annual-election input + live preview, used by BOTH employee
// enrollment surfaces (the EnrollmentWizard FSA step and the Benefits.tsx
// new-hire/modify plan card). Before this, the input, per-paycheck preview,
// range hint, employer-contribution note, and over-cap warning were implemented
// twice and drifted (DEV-174). This is the single source of truth.
//
// It assumes the plan is configured and the pay schedule is ready — callers
// still render their own "not configured" / "no pay schedule" messages and only
// mount this when an election can actually be entered. The math comes from the
// already-tested computeFsaPreview (client/src/lib/fsaPreview.ts).
import { DollarSign } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  computeFsaPreview,
  type FsaConfigForPreview,
  type FsaPreviewOptions,
} from "@/lib/fsaPreview";

interface FsaElectionInputProps {
  /** Raw annual election string (kept as a string so the field stays editable). */
  value: string;
  onChange: (value: string) => void;
  fsaConfig: FsaConfigForPreview | null | undefined;
  payCycle?: string | null;
  /** Proration (divide over the exact remaining pay periods). */
  prorationOptions?: FsaPreviewOptions;
  /** Dense styling for the plan-card context; default is the roomier wizard step. */
  compact?: boolean;
  inputId?: string;
}

export function FsaElectionInput({
  value,
  onChange,
  fsaConfig,
  payCycle,
  prorationOptions,
  compact = false,
  inputId = "annual-election",
}: FsaElectionInputProps) {
  const preview = computeFsaPreview(value, fsaConfig, payCycle, prorationOptions);
  const min = fsaConfig?.annualElectionMin != null ? Number(fsaConfig.annualElectionMin) : 0;
  const max = fsaConfig?.annualElectionMax != null ? Number(fsaConfig.annualElectionMax) : 0;

  if (compact) {
    return (
      <div className="space-y-1">
        <Label htmlFor={inputId} className="text-xs font-medium">
          Annual election ($)
        </Label>
        <Input
          id={inputId}
          type="number"
          inputMode="decimal"
          min={min || undefined}
          max={max || undefined}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={`Between $${min} and $${max}`}
          className={cn("h-8 text-sm", preview.overCap && "border-red-400 focus-visible:ring-red-400")}
        />
        <div className="text-[11px] text-muted-foreground">
          Plan range: ${min.toLocaleString()} – ${max.toLocaleString()}
          {preview.employerContribution > 0 && (
            <> · Employer adds ${preview.employerContribution.toLocaleString()}/yr</>
          )}
        </div>
        {preview.employeeAnnualIsValid && !preview.overCap && (
          <div className="text-[11px]">
            <span className="font-mono font-semibold">
              ${preview.perPaycheckEmployeeContribution.toFixed(2)} {preview.perPaycheckLabel}
            </span>
            {preview.prorated ? (
              <span className="text-muted-foreground">
                {" "}— ${preview.employeeAnnual.toLocaleString()} over {preview.periodsUsed} remaining paychecks
              </span>
            ) : (
              <span className="text-muted-foreground">
                {" "}— ${preview.employeeAnnual.toLocaleString()} ÷ {preview.periodsUsed} paychecks/yr
              </span>
            )}
          </div>
        )}
        {preview.overCap && (
          <div className="text-[11px] text-red-600 font-medium">
            Exceeds the plan maximum of ${preview.annualCap!.toLocaleString()}. Lower the amount to continue.
          </div>
        )}
      </div>
    );
  }

  // Roomier wizard-step layout.
  return (
    <div>
      <Label htmlFor={inputId}>Annual Election Amount</Label>
      <div className="flex items-center gap-2 mt-1">
        <DollarSign className="h-4 w-4 text-muted-foreground" />
        <Input
          id={inputId}
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={fsaConfig ? `$${min} - $${max || "N/A"}` : "Enter amount"}
          min={min || 0}
          max={max || undefined}
        />
      </div>
      {fsaConfig && (
        <p className="text-xs text-muted-foreground mt-1">
          Min: ${min.toLocaleString()} | Max: ${max.toLocaleString()}
          {preview.employerContribution > 0 && (
            <> · Employer contributes ${preview.employerContribution.toLocaleString()}/year</>
          )}
        </p>
      )}
      {preview.employeeAnnualIsValid && (
        <div className="mt-3 space-y-2">
          <div className="rounded-md border bg-muted/40 px-3 py-2 text-xs">
            <div className="font-mono font-semibold">
              {preview.perPaycheckEmployeeContribution.toLocaleString(undefined, {
                style: "currency",
                currency: "USD",
              })}
              <span className="text-muted-foreground font-normal"> {preview.perPaycheckLabel}</span>
            </div>
            {preview.prorated && (
              <div className="text-muted-foreground mt-0.5">
                Spread over {preview.periodsUsed} remaining paychecks this plan year
              </div>
            )}
            {preview.employerContribution > 0 && (
              <div className="text-muted-foreground mt-0.5">
                Combined annual: ${preview.combinedAnnual.toLocaleString()} (employee + employer)
              </div>
            )}
          </div>
          {preview.overCap && (
            <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              Combined employer (${preview.employerContribution.toLocaleString()}) + employee (${preview.employeeAnnual.toLocaleString()}) exceeds the plan max (${preview.annualCap!.toLocaleString()}).
            </div>
          )}
        </div>
      )}
    </div>
  );
}
